import _ from 'lodash';
import {
  axiosClient,
  createUnauthenticatedAxiosClient,
} from '../api/axiosClient';
import { getOrgId, getUserId, isTeamUser } from './subscription';
import { AxiosError } from 'axios';
import { AccountType } from '../enums/account';
import { UserRole } from '../enums/userRoles';
import { repackageAxiosError } from '../utils/errorUtils';
import {
  Team,
  TeamUser,
  TeamWithSubTeams,
  TeamInvitation,
  InvitationDetails,
} from '../interfaces/team';

export interface OrganizationUserData {
  id: string;
  cirra_user_id: string;
  role: UserRole;
}

export interface OrganizationData {
  team: Team | null;
  organization_users: OrganizationUserData[];
  // full_users_limit: number;
  // full_users_count: number;
  // full_users_remaining: number;
}

export const getUserAccountType = (): AccountType => {
  return isTeamUser() ? AccountType.Team : AccountType.Personal;
};

interface GetOrganizationUsersParams {
  orgId: string;
  withUsers?: boolean;
}

export const getOrganizationUsers = async ({
  orgId,
  withUsers = false,
}: GetOrganizationUsersParams): Promise<OrganizationData> => {
  try {
    if (!orgId) {
      throw new Error('Organization ID not found');
    }

    const tasks = [
      axiosClient.get(`/accounts/organizations/${orgId}`),
      // getOrganizationStats(orgId),
    ];
    if (withUsers) {
      tasks.push(axiosClient.get(`/accounts/organizations/${orgId}/users`));
    }
    const tasksResults = await Promise.all(tasks);

    const [
      teamRes,
      // orgStats
    ] = tasksResults;

    const result = {
      team: teamRes.data,
      organization_users: [],
      // full_users_limit: orgStats.full_users_limit,
      // full_users_count: orgStats.full_users_count,
      // full_users_remaining: orgStats.full_users_remaining,
    };
    if (withUsers) {
      const usersRes = tasksResults[tasksResults.length - 1];
      result.organization_users = usersRes.data;
    }
    return result;
  } catch (error) {
    const axiosError = error as AxiosError;
    throw new Error(
      (axiosError.response?.data as { message?: string })?.message ||
        'Failed to fetch organization users'
    );
  }
};

/*
export const getOrganizationStats = async (orgId: string) => {
  try {
    // const orgId = getOrgId();
    if (!orgId) {
      throw new Error('Organization ID not found');
    }
    const response = await axiosClient.get(
      `/subscriptions/organizations/${orgId}/stats`
    );
    return response.data;
  } catch (error) {
    throw repackageAxiosError(
      error as AxiosError,
      'Failed to fetch organization stats'
    );
  }
};
*/

export const updateUserRole = async (
  newRole: UserRole,
  userId: string
): Promise<OrganizationUserData> => {
  try {
    const orgId = getOrgId();
    if (!orgId) {
      throw new Error('Organization ID not found');
    }
    const response = await axiosClient.patch(
      `/accounts/organizations/${orgId}/users/${userId}/role`,
      { role: newRole }
    );
    return response.data;
  } catch (error) {
    throw repackageAxiosError(
      error as AxiosError,
      'Failed to update user roles'
    );
  }
};

/*

export const updateUserLicense = async (
  newLicense: OrganizationLicense,
  userId: string
): Promise<OrganizationUserData> => {
  try {
    const orgId = getOrgId();
    if (!orgId || !userId) {
      throw new Error('Organization ID or User ID not found');
    }
    const response = await axiosClient.patch(
      `/accounts/organizations/${orgId}/users/${userId}/license`,
      { license: newLicense }
    );
    return response.data;
  } catch (error) {
    throw repackageAxiosError(
      error as AxiosError,
      'Failed to update user license'
    );
  }
};
 */

export const deleteUser = async (
  orgId: string,
  userId: string
): Promise<OrganizationData> => {
  try {
    if (!orgId || !userId) {
      throw new Error('Team ID or User ID are required');
    }
    await axiosClient.delete(
      `/accounts/organizations/${orgId}/users/${userId}`
    );
    return getOrganizationUsers({ orgId, withUsers: true });
  } catch (error) {
    throw repackageAxiosError(error as AxiosError, 'Failed to delete user');
  }
};

export const getUserInfo = async () => {
  try {
    const userId = getUserId();
    if (!userId) {
      throw new Error('User ID not found');
    }
    const response = await axiosClient.get(`/accounts/users/${userId}`);
    return response.data;
  } catch (error) {
    throw repackageAxiosError(error as AxiosError, 'Failed to fetch user info');
  }
};

export const getUserRole = (user: TeamUser | undefined) => {
  return getNameOfRole(user?.role || null);
};

export const getNameOfRole = (role: UserRole | null | undefined) => {
  return (role || '').replace('Company ', '');
};

export const getTeamName = (team: Team | null | undefined): string => {
  if (!team) {
    return '';
  }

  return (
    team.cirra_organization_name || `Unnamed (${team.cirra_organization_id})`
  );
};

/*
export const validateParentTeamLicense = async (
  parentTeamId: string,
  allTeams: Team[]
): Promise<boolean> => {
  try {
    // Find the parent team to get its current_user for auth context
    const parentTeam = allTeams.find((team) => team.id === parentTeamId);
    if (!parentTeam || !parentTeam.current_user) {
      throw new Error('Parent team not found or no access');
    }

    // Make API call with parent team's auth context
    const response = await makeApiCall({
      url: `/subscriptions/organizations/${parentTeamId}/stats`,
      method: 'GET',
      asUser: parentTeam.current_user,
    });

    // Check if parent team has license remaining
    return response.data.full_users_remaining > 0;
  } catch (error) {
    console.error('Failed to validate parent team license:', error);
    throw new Error('Failed to validate parent team license');
  }
};
*/

export const groupTeams = (teams: Team[]) => {
  const masterTeams = teams.filter((team) => !team.master_organization_id);
  const subTeams = teams.filter((team) => team.master_organization_id);

  // Create a set of master team IDs for quick lookup
  const accessibleMasterTeamIds = new Set(masterTeams.map((team) => team.id));

  // Group sub teams by their master team ID
  const subTeamsByMasterId = _.groupBy(subTeams, 'master_organization_id');
  const teamsWithChildren = masterTeams.map((masterTeam) => ({
    ...masterTeam,
    subTeams: subTeamsByMasterId[masterTeam.id] || null,
  }));

  // Handle sub teams whose master team is not accessible
  const subTeamsWithUnaccessibleMasterTeam = subTeams.filter(
    (subTeam) =>
      subTeam.master_organization_id &&
      !accessibleMasterTeamIds.has(subTeam.master_organization_id)
  );
  const subTeamsWithUnaccessibleMasterTeamByMasterTeamId = _.groupBy(
    subTeamsWithUnaccessibleMasterTeam,
    'master_organization_id'
  );
  const unaccessibleMasterTeams = Object.values(
    subTeamsWithUnaccessibleMasterTeamByMasterTeamId
  ).map((subTeams: Team[]) => {
    const masterOrg = subTeams[0]?.master_organization;
    return {
      // Provide default values for required Team properties when master_organization is null/undefined
      id: masterOrg?.id || subTeams[0]?.master_organization_id || '',
      cirra_organization_id:
        masterOrg?.cirra_organization_id ||
        subTeams[0]?.master_organization_id ||
        '',
      cirra_organization_name:
        masterOrg?.cirra_organization_name || 'Unnamed Team',
      stripe_customer_id: masterOrg?.stripe_customer_id || null,
      createdAt:
        masterOrg?.createdAt ||
        subTeams[0]?.createdAt ||
        new Date().toISOString(),
      updatedAt:
        masterOrg?.updatedAt ||
        subTeams[0]?.updatedAt ||
        new Date().toISOString(),
      master_organization_id: masterOrg?.master_organization_id,
      master_organization: masterOrg,
      current_user: masterOrg?.current_user,
      subTeams: subTeams,
    } as TeamWithSubTeams;
  });

  const groupedTeams = [...teamsWithChildren, ...unaccessibleMasterTeams];
  const sortedTeams = _.orderBy(groupedTeams, ['createdAt'], ['desc']);
  return sortedTeams;
};

/**
 * Send a setup reminder email to the current user
 */
export const sendSetupLink = async (): Promise<void> => {
  try {
    await axiosClient.post('/accounts/users/me/send-setup-link');
  } catch (error) {
    throw repackageAxiosError(error as AxiosError, 'Failed to send setup link');
  }
};

// ----------------------------
// Team settings API functions
// ----------------------------

/**
 * Update team settings (e.g., team_only_invitations)
 */
export const updateTeamSettings = async (
  orgId: string,
  settings: { team_only_invitations?: boolean }
): Promise<Team> => {
  try {
    const response = await axiosClient.patch(
      `/accounts/organizations/${orgId}/settings`,
      settings
    );
    return response.data;
  } catch (error) {
    throw repackageAxiosError(
      error as AxiosError,
      'Failed to update team settings'
    );
  }
};

// ----------------------------
// Invitation API functions
// ----------------------------

/**
 * Send an invitation to join a team
 */
export const sendInvitation = async (params: {
  orgId: string;
  email: string;
  role: UserRole;
}): Promise<TeamInvitation> => {
  try {
    const response = await axiosClient.post(
      `/accounts/organizations/${params.orgId}/invitations`,
      { email: params.email, role: params.role }
    );
    return response.data;
  } catch (error) {
    throw repackageAxiosError(error as AxiosError, 'Failed to send invitation');
  }
};

/**
 * Get all pending invitations for an organization
 */
export const getOrganizationInvitations = async (
  orgId: string
): Promise<TeamInvitation[]> => {
  try {
    const response = await axiosClient.get(
      `/accounts/organizations/${orgId}/invitations`
    );
    return response.data;
  } catch (error) {
    throw repackageAxiosError(
      error as AxiosError,
      'Failed to fetch invitations'
    );
  }
};

/**
 * Resend an invitation email
 */
export const resendInvitation = async (
  orgId: string,
  invitationId: string
): Promise<TeamInvitation> => {
  try {
    const response = await axiosClient.post(
      `/accounts/organizations/${orgId}/invitations/${invitationId}/resend`
    );
    return response.data;
  } catch (error) {
    throw repackageAxiosError(
      error as AxiosError,
      'Failed to resend invitation'
    );
  }
};

/**
 * Withdraw (cancel) an invitation
 */
export const withdrawInvitation = async (
  orgId: string,
  invitationId: string
): Promise<void> => {
  try {
    await axiosClient.delete(
      `/accounts/organizations/${orgId}/invitations/${invitationId}`
    );
  } catch (error) {
    throw repackageAxiosError(
      error as AxiosError,
      'Failed to withdraw invitation'
    );
  }
};

/**
 * Get invitation details by token (public endpoint - no auth required)
 */
export const getInvitationByToken = async (
  token: string
): Promise<InvitationDetails> => {
  // Use unauthenticated client since this is a public endpoint
  const publicClient = createUnauthenticatedAxiosClient();
  try {
    const response = await publicClient.get(`/accounts/invitations/${token}`);
    return response.data;
  } catch (error) {
    throw repackageAxiosError(
      error as AxiosError,
      'Invitation not found or has expired'
    );
  }
};

/**
 * Accept an invitation
 */
export const acceptInvitation = async (
  token: string
): Promise<{ team: Team; user: TeamUser }> => {
  try {
    const response = await axiosClient.post(
      `/accounts/invitations/${token}/accept`
    );
    return response.data;
  } catch (error) {
    throw repackageAxiosError(
      error as AxiosError,
      'Failed to accept invitation'
    );
  }
};

/**
 * Accept an invitation by token (public endpoint — no auth required).
 * The invitation token serves as proof of authorization.
 *
 * For existing users: adds them to the team immediately.
 * For new users: returns null (user needs to sign up first; backend auto-accepts during subscription creation).
 */
export const acceptInvitationByToken = async (
  token: string
): Promise<{ team: Team; user: TeamUser } | null> => {
  const publicClient = createUnauthenticatedAxiosClient();
  try {
    const response = await publicClient.post(
      `/accounts/invitations/${token}/accept-by-token`
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    // 404 means user doesn't have an account yet — expected for new signups
    if (axiosError.response?.status === 404) {
      return null;
    }
    throw repackageAxiosError(axiosError, 'Failed to accept invitation');
  }
};
